import { z } from "zod";

/**
 * Zod schemas mirroring the FastAPI Pydantic models in
 * [permitpilot/api/app.py](../../../permitpilot/api/app.py). These are
 * the only place Python and TypeScript types meet, and they're trivial
 * to keep aligned because the request shapes are stable post-Ring 2B.
 */

// ---- Document section (response) ------------------------------------------

export const sourceTagSchema = z.enum([
  "llm",
  "llm_refined",
  "template",
  "placeholder",
  "boilerplate",
]);
export type SourceTag = z.infer<typeof sourceTagSchema>;

export const sectionTypeSchema = z.enum(["prose", "table", "form"]).or(z.string());

export const documentSectionSchema = z.object({
  section_id: z.string(),
  title: z.string(),
  content: z.string(),
  source: sourceTagSchema.optional().nullable(),
  section_type: z.string().optional().nullable(),
  facts_used: z.array(z.string()).optional().default([]),
  // Opći-dio izjave: variable names still awaiting a human (rendered as the
  // "[ ZA RUČNI UNOS ]" marker). Drives the per-izjava "nedostaje" bar. Left
  // optional (no default) so demo fixtures / legacy sections needn't carry it.
  missing: z.array(z.string()).optional(),
  // Sprint B — present after backend stamps GlobalIds for IFC-derived facts.
  fact_globalids: z.record(z.array(z.string())).optional(),
  // P2 — user lock: protects this section from selective regeneration on amend.
  locked: z.boolean().optional(),
  // S3 — klasa sadržaja: "A" (činjenice), "B" (standardni stručni tekst —
  // amber "generirano — pregledaj"), "C" (projektna proza), "" legacy.
  content_class: z.string().optional(),
});
export type DocumentSection = z.infer<typeof documentSectionSchema>;

export const validationIssueSchema = z.object({
  section_id: z.string(),
  message: z.string(),
  code: z.string().optional().nullable(),
});
export type ValidationIssue = z.infer<typeof validationIssueSchema>;

export const validationSchema = z.object({
  is_valid: z.boolean().default(true),
  errors: z.array(validationIssueSchema).default([]),
  warnings: z.array(validationIssueSchema).default([]),
});
export type Validation = z.infer<typeof validationSchema>;

export const factSheetSchema = z
  .object({
    _sources: z.record(z.string()).optional(),
    _sources_globalids: z.record(z.array(z.string())).optional(),
    compliance_checks: z
      .array(
        z.object({
          parameter: z.string(),
          actual: z.union([z.number(), z.string(), z.null()]),
          limit: z.union([z.number(), z.string(), z.null()]),
          check_type: z.string().optional(),
          status: z.string().optional(),
        }),
      )
      .optional(),
  })
  .catchall(z.unknown());
export type FactSheet = z.infer<typeof factSheetSchema>;

// P2 — what an amend changed: which inputs differed and how each section was
// treated (regenerated / protected-because-edited / left unchanged).
export const amendmentPlanSchema = z.object({
  changed_inputs: z.array(z.string()).default([]),
  regenerate: z.array(z.string()).default([]),
  protected: z.array(z.string()).default([]),
  unchanged: z.array(z.string()).default([]),
  has_changes: z.boolean().default(false),
});
export type AmendmentPlan = z.infer<typeof amendmentPlanSchema>;

export const docgenResponseSchema = z.object({
  status: z.string().optional(),
  elapsed_seconds: z.number().optional(),
  validation: validationSchema,
  fact_sheet: factSheetSchema,
  izjave: z.array(documentSectionSchema).default([]),
  naslovnice: z.array(documentSectionSchema).default([]),
  tekstualni_dio: z.array(documentSectionSchema).default([]),
  tables: z.array(documentSectionSchema).default([]),
  // R8 — Popis mapa + Sadržaj Mape 1; empty for projects without mape.
  mape: z.array(documentSectionSchema).default([]),
  // Merged opći-dio variables the izjave were rendered from — fed back to
  // ``/api/opci-dio/izjava-pdf`` and the export so the office PDF matches.
  opci_variables: z.record(z.unknown()).default({}),
  // Present only on an amend response — summary of what the update touched.
  amendment_plan: amendmentPlanSchema.optional(),
});
export type DocgenResponse = z.infer<typeof docgenResponseSchema>;

// P2 amend request — the generate request plus the previous document and the
// input snapshot it was generated from (fact sheet ∪ opći-dio variables).
export type AmendRequestPayload = DocumentGenerationRequest & {
  previous_sections: DocumentSection[];
  previous_fact_sheet: FactSheet;
  previous_opci_variables: Record<string, unknown>;
};

// ---- Document generation (request) ----------------------------------------

// One citation pinned by the architect in the Step-2 backbone panel.
// Travels with the generate request; the backend re-grounds it against
// the corpus before it touches the izjava (cite-or-abstain).
export const confirmedCitationSchema = z.object({
  instrument_id: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  article: z.string().nullable().optional(),
  source: z.string(),
  grounded: z.boolean().optional(),
});
export type ConfirmedCitation = z.infer<typeof confirmedCitationSchema>;

export const legalConfirmationPayloadSchema = z.object({
  confirmed_at: z.string(),
  citations: z.array(confirmedCitationSchema),
});
export type LegalConfirmationPayload = z.infer<typeof legalConfirmationPayloadSchema>;

export const documentGenerationRequestSchema = z.object({
  project_number: z.string(),
  project_date: z.string(),
  project_location: z.string(),
  investitor_name: z.string(),
  investitor_address: z.string(),
  investitor_oib: z.string(),
  // R5–R7 (additive; empty/null = today's single-investitor render).
  investitori: z
    .array(
      z.object({
        name: z.string().default(""),
        address: z.string().default(""),
        oib: z.string().default(""),
      }),
    )
    .default([]),
  suradnici: z
    .array(
      z.object({ ime: z.string().default(""), titula: z.string().default("") }),
    )
    .default([]),
  parcelacija: z
    .object({
      novi_broj: z.string().default(""),
      maticne: z.array(z.string()).default([]),
      ko: z.string().default(""),
    })
    .nullable()
    .default(null),
  mape: z.array(z.record(z.unknown())).default([]),
  zajednicka_oznaka: z.string().default(""),
  // S1 — generalije: faza izrade + strukovna odrednica (header rows).
  faza: z.string().default(""),
  strukovna_odrednica: z.string().default(""),
  // S3 — špranca id/model; null = današnji registar sekcija.
  spranca: z.union([z.string(), z.record(z.unknown())]).nullable().optional(),
  ured_name: z.string(),
  ured_address: z.string(),
  glavni_projektant_name: z.string(),
  glavni_projektant_title: z.string(),
  glavni_projektant_license: z.string(),
  direktor_name: z.string(),
  katastarska_cestica: z.string(),
  katastarski_opcina: z.string(),
  cestica_povrsina_m2: z.number(),
  building_type: z.string(),
  building_subtype: z.string().nullable().optional(),
  work_type: z.string().default("novogradnja"),
  zahvat_opis: z.string().default(""),
  katnost: z.string().default(""),
  bruto_areas: z.record(z.number()).default({}),
  tlocrtna_projekcija_m2: z.number().default(0),
  building_length_m: z.number().default(0),
  building_width_m: z.number().default(0),
  building_height_v_m: z.number().default(0),
  building_height_V_m: z.number().default(0),
  num_stambenih: z.number().int().default(0),
  num_poslovnih: z.number().int().default(0),
  num_parking: z.number().int().default(0),
  zelena_povrsina_m2: z.number().default(0),
  udaljenost_sjever_m: z.number().nullable().optional(),
  udaljenost_jug_m: z.number().nullable().optional(),
  udaljenost_istok_m: z.number().nullable().optional(),
  udaljenost_zapad_m: z.number().nullable().optional(),
  regulacijski_pravac_m: z.number().nullable().optional(),
  context: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  in_city_center: z.boolean().default(false),
  // Phase 2 W3 / R1 — propagated from the feasibility step so docgen reads
  // the same spatial plan the user was just analysed against. "" = unknown;
  // the backend resolves k.o.→JLS or renders the plan-missing placeholder.
  municipality_code: z.string().default(""),
  // IFC depth (Ring 2B) — passed through opaquely; backend validates.
  openings: z.array(z.record(z.unknown())).default([]),
  materials: z.array(z.record(z.unknown())).default([]),
  rooms_with_polygons: z.array(z.record(z.unknown())).default([]),
  building_footprint_polygon: z.array(z.unknown()).default([]),
  extraction_warnings: z.array(z.string()).default([]),
  // B1 — vision-over-nacrti (opaque pass-through). ``nacrt_opis`` = per-drawing
  // vision output; ``nacrt_confirmed_facts`` = proposed facts the architect
  // confirmed. Empty → docgen byte-identical to today.
  nacrt_opis: z.array(z.record(z.unknown())).default([]),
  nacrt_confirmed_facts: z.array(z.record(z.unknown())).default([]),
  // Step-2 legal sign-off — null when the architect didn't confirm.
  legal_confirmation: legalConfirmationPayloadSchema.nullable().optional(),
  // Canonical ProjectModel inputs — same shapes /api/opci-dio/status takes,
  // so generation consumes the model the completeness panel displayed.
  situacija: z.record(z.unknown()).nullable().optional(),
  recognized_docs: z.array(z.record(z.unknown())).default([]),
  variable_sources: z.array(z.record(z.unknown())).nullable().optional(),
  special_condition_bodies: z.array(z.string()).nullable().optional(),
  // P3 — office letterhead identity (name_full / tagline / contact).
  office: z.record(z.unknown()).nullable().optional(),
});
export type DocumentGenerationRequest = z.infer<
  typeof documentGenerationRequestSchema
>;

// ---- B1: vision-over-nacrti -------------------------------------------------

/** One vision-proposed qualitative fact — amber until the architect confirms
 *  it (propose-then-confirm). ``low_trust`` mirrors the backend 0.69 cap. */
export const proposedFactSchema = z.object({
  kljuc: z.string(),
  vrijednost: z.string(),
  tip: z.string().default("kvalitativno"),
  citat_vizualni: z.string().default(""),
  confidence: z.number().default(0),
  low_trust: z.boolean().default(false),
});
export type ProposedFact = z.infer<typeof proposedFactSchema>;

/** ``/api/describe-drawing`` response. ``skipped`` marks a non-drawing type;
 *  ``error`` (set with empty prose) means "degrade to deterministic-only". */
export const drawingVisionResultSchema = z.object({
  status: z.string().default("ok"),
  skipped: z.boolean().optional(),
  doc_type: z.string(),
  opis_prose: z.string().default(""),
  predlozeni_fakti: z.array(proposedFactSchema).default([]),
  provenance: z.record(z.unknown()).default({}),
  error: z.string().nullable().optional(),
});
export type DrawingVisionResult = z.infer<typeof drawingVisionResultSchema>;

// P3 — the architect's office identity, kept globally (one office per user).
// A ``type`` (not ``interface``) so it stays assignable to the request
// payloads' ``Record<string, unknown>`` office field.
export type OfficeIdentity = {
  // Letterhead (footer of every document).
  name_full: string;
  tagline: string;
  contact: string;
  // Izjava body identity — the fields identical on every project (ured /
  // direktor / glavni projektant + HKA licence). Set once here, the backend
  // fills them into every project's izjave (see ``office_izjava_variables``).
  // Optional so offices persisted before this addition stay valid.
  ured_addr?: string;
  direktor_name?: string;
  gp_name_full?: string;
  hka_klasa?: string;
  hka_urbroj?: string;
  hka_broj?: string;
  hka_date?: string;
};

// ---- Refine / regenerate / export -----------------------------------------

export const refineRequestSchema = z.object({
  section_id: z.string(),
  current_content: z.string(),
  instruction: z.string().min(1),
  fact_sheet: factSheetSchema,
  section_type: z.string().default("prose"),
  project_id: z.string().nullable().optional(),
});
export type RefineRequest = z.infer<typeof refineRequestSchema>;

export const refineResponseSchema = z
  .object({
    refined_content: z.string().optional(),
    content: z.string().optional(),
    text: z.string().optional(),
    summary: z.string().optional(),
  })
  .catchall(z.unknown());
export type RefineResponse = z.infer<typeof refineResponseSchema>;

export const regenerateRequestSchema = z.object({
  section_id: z.string(),
  fact_sheet: factSheetSchema,
});
export type RegenerateRequest = z.infer<typeof regenerateRequestSchema>;

export const regenerateResponseSchema = z.object({
  status: z.string(),
  message: z.string().optional(),
  section: documentSectionSchema.optional(),
});
export type RegenerateResponse = z.infer<typeof regenerateResponseSchema>;

export const exportRequestSchema = z.object({
  format: z.enum(["docx", "pdf"]),
  sections: z.array(documentSectionSchema),
  fact_sheet: factSheetSchema.nullable().optional(),
  tab_name: z.string().nullable().optional(),
  variables: z.record(z.unknown()).nullable().optional(),
  // P3 — office letterhead identity, so the export matches the document.
  office: z.record(z.unknown()).nullable().optional(),
  // S2 — špranca id ("ured_generic" / "komora") ili puni model; null =
  // Permitko standard (današnji izgled).
  spranca: z.union([z.string(), z.record(z.unknown())]).nullable().optional(),
});
export type ExportRequest = z.infer<typeof exportRequestSchema>;

// ---- Feasibility (Step 1) -------------------------------------------------

export const corpusStatsSchema = z.object({
  ppugo_articles: z.number().int().nullable().optional(),
  spatial_articles_total: z.number().int().nullable().optional(),
  law_articles: z.number().int().nullable().optional(),
  law_collection: z.number().int().nullable().optional(),
  planning_designations: z.number().int().nullable().optional(),
  municipalities: z.number().int().nullable().optional(),
  curated_nn_chains: z.number().int().nullable().optional(),
  gtc_count: z.number().int().nullable().optional(),
  default_municipality: z.string().nullable().optional(),
  // L9 — snapshot freshness fields surfaced on the proof bar.
  law_instruments_synced: z.number().int().nullable().optional(),
  applicability_rules: z.number().int().nullable().optional(),
  last_sync_finished_at: z.string().nullable().optional(),
  // Production "je li dopušteno" limits (each carries its plan citation).
  construction_rules: z.number().int().nullable().optional(),
});
export type CorpusStats = z.infer<typeof corpusStatsSchema>;

export const feasibilityRequestSchema = z.object({
  building_type: z.string(),
  building_subtype: z.string().optional().nullable(),
  context: z.string().optional().nullable(),
  designation: z.string().optional().nullable(),
  in_city_center: z.boolean().default(false),
  work_type: z.string().default("novogradnja"),
  // PV — kod provizornog verdikta općina se razrješava iz plana (draft),
  // pa zahtjev može doći bez nje; backend je ionako Optional[str].
  municipality_code: z.string().optional().nullable(),
  // PV — verdikt nad UČITANIM (draft) planom: staged, još nepotvrđena pravila
  // iz tog plana umjesto produkcijskih. Odgovor nosi provisional:true.
  provisional_plan_id: z.number().int().optional().nullable(),
});
export type FeasibilityRequest = z.infer<typeof feasibilityRequestSchema>;

// --- G2: chat ulaz u modul gradivosti -------------------------------------
// Mirrors ``POST /api/gradivost/chat`` (permitpilot/api/gradivost.py):
// parse + auto-zona (G0) + potpitanje; verdikt zatim vrti postojeći
// /api/feasibility s hidratiziranom formom.

export const gradivostZoneShareSchema = z
  .object({
    oznaka: z.string(),
    udio_pct: z.number(),
    plan_izvor: z.string().optional().nullable(),
    primarna: z.string().optional().nullable(),
    izgradjenost: z.string().optional().nullable(),
    /** Naselje iz plana — nosi se u projekt kao lokacija zahvata (P1-6). */
    naselje: z.string().optional().nullable(),
  })
  .passthrough();

export const gradivostChatResponseSchema = z
  .object({
    parsed: z
      .object({
        kc: z.string().nullable(),
        ko: z.string().nullable(),
        municipality_code: z.string().nullable(),
        municipality_name: z.string().nullable().optional(),
        plan_version: z.string().nullable().optional(),
        designation: z.string().nullable(),
        designation_source: z.enum(["user", "auto"]).nullable(),
        building_type: z.string().nullable(),
        work_type: z.string().nullable(),
        in_city_center: z.boolean(),
        context: z.string(),
      })
      .passthrough(),
    zone: z
      .object({
        status: z.string(),
        zones: z.array(gradivostZoneShareSchema).default([]),
        message: z.string().nullable().optional(),
        cached: z.boolean().optional(),
        // G0 vraća površinu čestice iz DGU-a — G1 je cross-checkira protiv ZK.
        parcel: z.object({ area_m2: z.number().nullable() }).nullable().optional(),
      })
      .passthrough()
      .nullable(),
    missing: z.array(z.string()),
    question: z.string().nullable(),
    /** P1-4: k.o. je prepoznata, ali njen plan nije u bazi — frontend nudi
     *  upload plana (postojeći wizard) umjesto ponavljanja potpitanja. */
    plan_missing_ko: z.string().nullable().optional(),
    /** P2: upit je izvan domene modula — `question` tada objašnjava što
     *  modul radi umjesto da traži općinu. */
    off_topic: z.boolean().optional(),
    /** Sloj pokrivenosti: A = plan+auto-zona (PGŽ), B = plan u bazi bez
     *  auto-zone (Poreč/Umag — treba zonu iz teksta/LI), C = plan nije u
     *  bazi (upload). Usmjerava fallback; `question` već nosi točan tekst. */
    coverage_tier: z.enum(["A", "B", "C"]).nullable().optional(),
    caveats: z.array(z.string()).default([]),
    log_id: z.number().int().nullable(),
  })
  .passthrough();
export type GradivostChatResponse = z.infer<typeof gradivostChatResponseSchema>;
export type GradivostZoneShare = z.infer<typeof gradivostZoneShareSchema>;

// Mirrors the dict returned by ``POST /api/feasibility`` in
// permitpilot/api/app.py. Lenient on LLM-shaped sub-payloads
// (blockers / conditions / findings come from the model and may be
// strings or objects), strict on the deterministic envelope.

export const feasibilityParamCheckSchema = z.object({
  parameter: z.string(),
  project_value: z.number().nullable().optional(),
  limit_value: z.number().nullable().optional(),
  limit_type: z.string().nullable().optional(),
  status: z.string(),
  source_article: z.union([z.string(), z.number()]).nullable().optional(),
});
export type FeasibilityParamCheck = z.infer<typeof feasibilityParamCheckSchema>;

/** M2 — Poreč-signal: tekst plana iz kojeg su čitani limiti zaostaje za
 *  najnovijom registarskom revizijom (informativni caveat verdikta). */
export const planFreshnessSchema = z.object({
  stale: z.boolean(),
  text_plan_id: z.number().nullable().optional(),
  text_revision: z.string().nullable().optional(),
  text_publishing_date: z.string().nullable().optional(),
  latest_plan_id: z.number().nullable().optional(),
  latest_label: z.string().nullable().optional(),
  latest_publishing_date: z.string().nullable().optional(),
  warning: z.string().nullable().optional(),
});
export type PlanFreshness = z.infer<typeof planFreshnessSchema>;

/** M4a — UPU/DPU gap-signal. L1: plan mjerodavne zone delegira detaljnu
 *  razradu na UPU/DPU (per-čestica, citirano). L2: općina ima registrirane
 *  UPU/DPU planove koje ne držimo (tiha napomena o pokrivenosti). Oba caveat
 *  — nikad ne mijenjaju presudu. */
export const upuGapSchema = z.object({
  l1: z
    .object({
      posredna: z.boolean(),
      articles: z
        .array(
          z.object({
            source_article: z.string(),
            source_text: z.string().nullable().optional(),
          }),
        )
        .default([]),
      warning: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  l2: z
    .object({
      count: z.number(),
      by_type: z.record(z.number()).default({}),
      examples: z.array(z.string()).default([]),
      warning: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type UpuGap = z.infer<typeof upuGapSchema>;

/** PV — staged pravilo koje NIJE ušlo u provizornu presudu (ne-grounded /
 *  derived / KIG-KISN izvan raspona). Prikazuje se „za provjeru" s doslovnim
 *  citatom (value_spans). Lenient — oblik prati construction_rules_staging. */
export const provisionalReviewRuleSchema = z
  .object({
    rule_subject_code: z.string().nullable().optional(),
    kig_max: z.number().nullable().optional(),
    kisn_max: z.number().nullable().optional(),
    max_floors: z.number().nullable().optional(),
    max_height_v_m: z.number().nullable().optional(),
    min_plot_size_m2: z.number().nullable().optional(),
    source_article: z.union([z.string(), z.number()]).nullable().optional(),
    grounding_status: z.string().nullable().optional(),
    value_spans: z.record(z.string()).nullable().optional(),
    derived: z.boolean().nullable().optional(),
  })
  .passthrough();
export type ProvisionalReviewRule = z.infer<typeof provisionalReviewRuleSchema>;

export const feasibilityResponseSchema = z
  .object({
    status: z.string(),
    elapsed_seconds: z.number().optional(),
    municipality_code: z.string().optional().nullable(),
    municipality_name: z.string().optional().nullable(),
    plan_version: z.string().optional().nullable(),
    plan_freshness: planFreshnessSchema.optional().nullable(),
    // M4a — UPU/DPU gap-signal (None kad ni L1 ni L2 nemaju signala).
    upu_gap: upuGapSchema.optional().nullable(),
    // PV — verdikt je proviziran (AI pročitao učitani plan, ljudski nepotvrđen).
    provisional: z.boolean().optional().default(false),
    provisional_plan_id: z.number().int().nullable().optional(),
    provisional_review_rules: z.array(provisionalReviewRuleSchema).default([]),
    outcome_class: z.string().default("UNKNOWN"),
    llm_outcome_class: z.string().optional().nullable(),
    decision_overrides: z.array(z.record(z.unknown())).default([]),
    pre_llm_blockers: z.array(z.record(z.unknown())).default([]),
    parameter_validation: z.array(feasibilityParamCheckSchema).default([]),
    memo_text: z.string().default(""),
    model_warning: z.string().nullable().optional(),
    llm_output: z
      .object({
        blockers: z.array(z.unknown()).default([]),
        conditions_and_limits: z.array(z.unknown()).default([]),
        key_findings: z.array(z.unknown()).default([]),
        disclaimer: z.string().nullable().optional(),
      })
      .passthrough()
      .default({}),
    bundle_stats: z.record(z.number()).optional(),
  })
  .passthrough();
export type FeasibilityResponse = z.infer<typeof feasibilityResponseSchema>;

/**
 * Trimmed feasibility result persisted on the project draft so Step 1
 * re-shows the last verdict when the architect comes back. Everything
 * is pre-stringified — the full ``llm_output`` stays out of
 * localStorage.
 *
 * Items carry the engine's plan citation (``source_reference``) — the
 * verdict's core promise. Drafts persisted before this shape existed
 * hold plain strings; renderers normalise via ``asCitedText``/
 * ``asLimitRow`` instead of a store migration.
 */

/** One cited statement (finding / blocker): text + plan citation. */
export interface FeasibilityCitedText {
  text: string;
  source: string | null;
}

/** One plan limit from ``llm_output.conditions_and_limits``. */
export interface FeasibilityLimitRow {
  name: string;
  value: string;
  context: string | null;
  source: string | null;
}

export interface FeasibilitySummary {
  outcome_class: string;
  municipality_name: string;
  plan_version: string;
  /** M2 — Poreč-signal caveat; null kad usporedba nije moguća ili je tekst svjež. */
  plan_freshness?: PlanFreshness | null;
  /** M4a — UPU/DPU gap-signal (L1 citirano + L2 tiha napomena); null bez signala. */
  upu_gap?: UpuGap | null;
  /** PV — verdikt nad učitanim planom (AI pročitao, ljudski nepotvrđen) → amber traka. */
  provisional?: boolean;
  /** PV — staged pravila „za provjeru" (nisu ušla u presudu); s doslovnim citatom. */
  provisional_review_rules?: ProvisionalReviewRule[];
  memo_text: string;
  parameter_validation: FeasibilityParamCheck[];
  blockers: Array<string | FeasibilityCitedText>;
  conditions: Array<string | FeasibilityLimitRow>;
  findings: Array<string | FeasibilityCitedText>;
  model_warning: string | null;
  checkedAt: string;
}

// ---- Legal backbone (Faza 2 — agentic-grounded hybrid) --------------------
// Mirrors permitpilot/core/legal_backbone.py::LegalBackbone.to_dict().

export const backboneCitationSchema = z.object({
  instrument_id: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  source: z.string(), // "spine" | "agent" | "engine_recall"
  grounded: z.boolean(),
  article: z.string().nullable().optional(),
  excerpt: z.string().nullable().optional(),
  quotable: z.boolean().optional(),
  reason: z.string().nullable().optional(),
});
export type BackboneCitation = z.infer<typeof backboneCitationSchema>;

export const legalBackboneResponseSchema = z.object({
  core: z.array(backboneCitationSchema),
  contextual: z.array(backboneCitationSchema),
  review_recall: z.array(backboneCitationSchema),
  engine_unquotable: z.array(backboneCitationSchema),
  needs_review: z.boolean(),
  degraded: z.boolean(),
  summary: z.object({
    core: z.number(),
    contextual: z.number(),
    review_recall: z.number(),
    engine_unquotable: z.number(),
    grounded_total: z.number(),
  }),
});
export type LegalBackboneResponse = z.infer<typeof legalBackboneResponseSchema>;

export interface LegalBackboneRequest {
  building_type: string;
  building_subtype?: string | null;
  context?: string | null;
  work_type?: string;
  designation?: string | null;
  model_features?: Record<string, boolean> | null;
  brief?: string | null;
}

// ---- Text-vs-law verification (Faza 3) ------------------------------------
// Mirrors permitpilot/core/legal_verifier.py::verify_text_against_law output.

export const verifyClaimSchema = z.object({
  claim: z.string().nullable().optional(),
  instrument_id: z.string().nullable().optional(),
  article: z.string().nullable().optional(),
  verdict: z.string(), // supported | unsupported | contradicts | article_missing | cant_verify
  grounded: z.boolean(),
  excerpt: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
});
export type VerifyClaim = z.infer<typeof verifyClaimSchema>;

export const verifyTextResponseSchema = z.object({
  claims: z.array(verifyClaimSchema),
  needs_review: z.boolean(),
  degraded: z.boolean().default(false),
  steps: z.number().optional(),
});
export type VerifyTextResponse = z.infer<typeof verifyTextResponseSchema>;

export interface VerifyTextRequest {
  text: string;
  candidate_instruments?: Array<{ instrument_id: string; title?: string | null }> | null;
}

// ---- Situacija (declared urbanistic iskaz) --------------------------------
// Mirrors permitpilot/extraction/situacija_params.py::build_situacija_report.

export const situacijaCheckSchema = z.object({
  parameter: z.string(),
  declared: z.number().nullable(),
  recomputed: z.number().nullable(),
  limit: z.number().nullable(),
  limit_type: z.string().nullable(),
  status: z.string(), // "OK" | "GRANIČNO" | "PREKORAČENO" | …
  note: z.string().default(""),
});
export type SituacijaCheck = z.infer<typeof situacijaCheckSchema>;

// Honest per-field summary of what the parser actually read — `empty`
// means "no core parameter was parsed" so the UI must not render success.
export const situacijaExtractionSchema = z.object({
  parsed: z.array(z.string()).default([]),
  missing: z.array(z.string()).default([]),
  empty: z.boolean().default(false),
  text_layer_chars: z.number().default(0),
});
export type SituacijaExtraction = z.infer<typeof situacijaExtractionSchema>;

export const situacijaReportSchema = z.object({
  file: z.string().optional(),
  params: z.record(z.unknown()).default({}),
  checks: z.array(situacijaCheckSchema).default([]),
  flags: z.array(situacijaCheckSchema).default([]),
  extraction: situacijaExtractionSchema.optional(),
});
export type SituacijaReport = z.infer<typeof situacijaReportSchema>;

// ---- Recognized opći-dio document (POST /api/recognize-document) ----------
// Mirrors permitpilot/extraction/document_recognizer.py::recognize_pdf output.
// The whole object is round-tripped verbatim into /api/opci-dio/status and
// /api/generate-documents ``recognized_docs`` — passthrough keeps it intact.

export const recognizedFieldSchema = z
  .object({
    value: z.unknown().nullable(),
    confidence: z.number().nullable().optional(),
  })
  .passthrough();

export const recognizedDocumentSchema = z
  .object({
    document_type: z.string(),
    target_slot: z.string().nullable().optional(),
    classification_confidence: z.number().nullable().optional(),
    reasoning: z.string().nullable().optional(),
    fields: z.record(recognizedFieldSchema).default({}),
    _source_file: z.string().nullable().optional(),
    // Token for the persisted original — fetch the source file via
    // ``GET /api/files/{doc_id}`` (see ``fetchFileBlob`` in client.ts).
    doc_id: z.string().nullable().optional(),
  })
  .passthrough();
export type RecognizedDocument = z.infer<typeof recognizedDocumentSchema>;

// ---- Opći-dio status (the canonical project model the situacija feeds) ----
// Slice of the rich payload: the SituacijaPanel reads ``project_model``;
// the CompletenessPanel reads ``rows`` + ``summary.readiness``.
// ``passthrough`` keeps the rest intact for other consumers.

export const opciDioSlotRowSchema = z
  .object({
    slot: z.string(),
    name: z.string(),
    klasa: z.string(),
    fulfilled_by: z.string(),
    required: z.boolean(),
    state: z.string(), // "prepoznato" | "za_generirati" | "nedostaje" | "nije_potrebno"
    evidence: z
      .array(
        z
          .object({
            file: z.string().nullable().optional(),
            document_type: z.string().nullable().optional(),
            confidence: z.number().nullable().optional(),
          })
          .passthrough(),
      )
      .default([]),
  })
  .passthrough();
export type OpciDioSlotRow = z.infer<typeof opciDioSlotRowSchema>;

export const readinessItemSchema = z
  .object({ kind: z.string(), label: z.string() })
  .passthrough();
export type ReadinessItem = z.infer<typeof readinessItemSchema>;

export const opciDioStatusSchema = z
  .object({
    rows: z.array(opciDioSlotRowSchema).default([]),
    project_model: z
      .object({
        variables: z.record(z.unknown()).default({}),
        provenance: z
          .record(
            z
              .object({ source: z.string().optional(), plane: z.string().optional() })
              .passthrough(),
          )
          .default({}),
        conflicts: z.array(z.record(z.unknown())).default([]),
        low_confidence: z.array(z.string()).default([]),
        required_pu_domains: z.array(z.string()).default([]),
      })
      .passthrough(),
    pu_domain_coverage: z
      .object({
        required: z.array(z.string()).default([]),
        covered: z.array(z.string()).default([]),
        missing: z.array(z.string()).default([]),
      })
      .passthrough()
      .optional(),
    summary: z
      .object({
        readiness: z
          .object({
            ready: z.boolean(),
            blockers: z.array(readinessItemSchema).default([]),
            warnings: z.array(readinessItemSchema).default([]),
          })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type OpciDioStatus = z.infer<typeof opciDioStatusSchema>;
